import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type Ref } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { Check, DotsHorizontal, RefreshCw01, XClose, Zap, ZapOff } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import type { DamageZone } from "../../types"
import {
  CAPTURE_COPY,
  phoneLine,
  shotAnnouncement,
  shotInstruction,
  shotTitle,
  VERDICT_LINE,
  walkLine,
  wrongSideQuestion,
} from "../capture-copy"
import type { VisionClient } from "../brain/vision-client"
import type { IntakeShotCapture } from "@/lib/api/public-consumer"
import { exampleFor } from "../examples"
import { angleDiff, bearingNow, judgeTurn, type TurnJudgement } from "../motion/turn"
import { useCameraYaw } from "../motion/use-camera-yaw"
import type { BodyType } from "../outlines"
import { useVinStep, VIN_COPY, vinAnnouncement, type VinActions } from "../vin/use-vin-step"
import { useFocusAfterCard, VinCard } from "../vin/vin-card"
import { loadVinReader } from "../vin/vin-reader"
import { localIssue, resultFor, type ResultState } from "../shot-result"
import { StandMap, standFor } from "../stand-map"
import { ViewPictogram } from "../view-pictogram"
import { preparePhoto } from "../uploads/prepare-photo"
import type { ShotUploads } from "../uploads/use-shot-uploads"
import type { CaptureState, Corner, PlannedShot, ShotId } from "../types"
import type { Insets } from "./target-frame"
import { QUALITY } from "./frame-metrics"
import { insetsFrom } from "./layout"
import { MoreSheet, type MoreItem } from "./more-sheet"
import { RotatePrompt } from "./rotate-prompt"
import { useOrientation } from "./use-orientation"
import { CarHighlight } from "./car-highlight"
import type { FrameMetrics } from "./frame-metrics"
import { walkRecorderEnabled, type StreamSource } from "./stream-source"
import { CameraProblem } from "./camera-problem"
import { AutoPhotoIntro } from "./auto-photo-intro"
import { COACH_LINE, wideLensLine } from "./coach"
import { useCamera, type Lens } from "./use-camera"
import { useLiveCoach } from "./use-live-coach"
import { guideBox, ViewfinderBrackets, type BracketTone } from "./viewfinder-brackets"
import { WalkCard } from "./walk-card"
import { speechAvailable, useVoiceCoach } from "./voice-coach"

// Dev-only: the real-walk recorder (?camrec=1). The MODE check is decided at
// build time, so a production bundle drops the panel and its import.
const WalkRecorderPanel =
  import.meta.env.MODE === "mock" || import.meta.env.MODE === "design"
    ? lazy(() => import("../dev/walk-recorder-panel"))
    : null

// The live camera for the whole walk. Black, full-bleed, frosted controls;
// green only ever means "good". One instruction at a time on a dark band (so
// it reads over any scene), one coaching line under it, three plain checks
// above the shutter. Whole-car photos outline the person's own car on the
// picture and mark the target with the eTX corners; it turns green and the
// photo takes itself when the car fits and is the corner asked for. A photo
// is never a dead end: every shot can be retaken, skipped or taken with the
// phone's camera.

const C = CAPTURE_COPY.coach
const AUTO_NEXT_MS = 1400

interface Strip {
  key: string
  metrics: FrameMetrics | null
}

// Rendered into <body>: the flow's animated panes use transforms, which
// would trap a position:fixed camera inside them. The page behind can't
// scroll while the camera is open.
export function CameraScreen(props: Parameters<typeof CameraSurface>[0]) {
  useEffect(() => {
    const html = document.documentElement
    const prev = html.style.overflow
    html.style.overflow = "hidden"
    return () => {
      html.style.overflow = prev
    }
  }, [])
  return createPortal(<CameraSurface {...props} />, document.body)
}

function CameraSurface({
  plan,
  queue,
  index,
  capture,
  damage,
  source,
  vision,
  debug,
  uploads,
  onAccept,
  onNext,
  onSkip,
  onClose,
  onUseNative,
  vin,
  bodyType,
  voice,
  onVoiceChange,
  introSeen,
  onIntroSeen,
}: {
  plan: readonly PlannedShot[]
  queue: readonly ShotId[]
  index: number
  capture: CaptureState
  damage: readonly DamageZone[]
  source: StreamSource
  // The vision engine (smart camera): finds, traces and checks the car.
  vision: VisionClient | null
  debug: boolean
  uploads: Pick<ShotUploads, "submit" | "retry" | "pending" | "online">
  onAccept: (key: string) => void
  onNext: () => void
  onSkip: (id: ShotId) => void
  onClose: () => void
  onUseNative: () => void
  // The VIN step (first in the walk): read, look up, confirm the car.
  vin: VinActions
  // The car's body type (from the VIN; a sedan otherwise), for the small
  // "this view" drawing.
  bodyType: BodyType
  // The "it takes the photo for you" intro has played this visit.
  introSeen: boolean
  onIntroSeen: () => void
  // Voice coach on/off (kept in the flow draft).
  voice: boolean
  onVoiceChange: (on: boolean) => void
}) {
  const shot = plan.find((s) => s.id === queue[index]) ?? null
  const prevShot = index > 0 ? plan.find((s) => s.id === queue[index - 1]) ?? null : null
  const reduce = useReducedMotion()
  const camera = useCamera({ active: true, source })
  const rootRef = useRef<HTMLDivElement>(null)
  const trayRef = useRef<HTMLButtonElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  const [walkCard, setWalkCard] = useState(true)
  const [strip, setStrip] = useState<Strip | null>(null)
  const [held, setHeld] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [flash, setFlash] = useState(false)
  const [flying, setFlying] = useState<{ url: string; to: DOMRect } | null>(null)
  const [showExample, setShowExample] = useState(false)
  // "More" is open: the camera waits (no auto-photo behind it).
  const [moreOpen, setMoreOpen] = useState(false)
  // The light button: once it's been dark this shot, it stays (no flicker).
  const [torchShown, setTorchShown] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const busy = useRef(false)
  // A photo is being taken (a still can take a moment on some phones)…
  const [taking, setTaking] = useState(false)
  // …and the camera is exposing it right now ("Hold still.").
  const [exposing, setExposing] = useState(false)
  const shotIdRef = useRef<string | null>(null)
  const [recorderOn] = useState(() => walkRecorderEnabled(new URLSearchParams(window.location.search)))

  // Track the screen size (brackets are positioned in pixels).
  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el) return
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight })
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // New shot → walk card, fresh strip.
  const shotId = shot?.id
  useLayoutEffect(() => {
    shotIdRef.current = shotId ?? null
  })
  useEffect(() => {
    setWalkCard(true)
    setStrip(null)
    setHeld(false)
    setAttempt(0)
    setError(null)
    setStandOpen(false)
    setMoreOpen(false)
    setTorchShown(false)
  }, [shotId])

  // Once per visit, before the camera first takes a photo by itself (only
  // the smart camera does): how that works. Decided at the start of a
  // whole-car photo, before any car is being framed (introFor, below); never
  // over a photo being taken or its result.
  const [introFor, setIntroFor] = useState<string | null>(null)
  const introUp = !!shot && introFor === shot.id && !introSeen && camera.status === "live" && !walkCard && !strip && !taking
  // The camera looks (and may take the photo itself) only with nothing over
  // it and no photo being taken.
  const running =
    camera.status === "live" && !walkCard && !strip && !showExample && !moreOpen && !taking && !introUp
  // Nothing to move away from: the walk's first photo, a retake, or the first
  // corner after the VIN (the door sticker isn't a spot to avoid).
  const firstShot = index === 0 || attempt > 0 || prevShot?.kind === "sticker"

  // The VIN step: the barcode is read live; a photo goes to the server.
  const vinStep = useVinStep(vin, onNext)
  const vinStepOn = shot?.kind === "sticker"
  // Reading / "Is it a …?" / typing: the card is the only thing to answer.
  const vinCardUp = vinStepOn && vinStep.state.kind !== "idle"
  const linksRef = useRef<HTMLDivElement>(null)
  useFocusAfterCard(vinCardUp, vinStepOn, linksRef)
  // What covers the picture (the instruction band above, the controls
  // below): the target for the whole car sits in the space between.
  // Upright: the band on top, the controls below. Sideways: the instruction
  // column on one side, the shutter rail on the other (layout.ts).
  const layout = useOrientation()
  const topRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [insets, setInsets] = useState<Insets>({ top: 0, right: 0, bottom: 0, left: 0 })
  // Measured when something changes size (not every frame).
  useLayoutEffect(() => {
    const measure = () => {
      const root = rootRef.current?.getBoundingClientRect()
      if (!root) return
      const covers = [topRef.current, bottomRef.current].flatMap((el) => (el ? [el.getBoundingClientRect()] : []))
      const next = insetsFrom(root, covers)
      setInsets((prev) =>
        prev.top === next.top && prev.bottom === next.bottom && prev.left === next.left && prev.right === next.right ? prev : next,
      )
    }
    measure()
    const ro = new ResizeObserver(measure)
    for (const el of [rootRef.current, topRef.current, bottomRef.current]) if (el) ro.observe(el)
    return () => ro.disconnect()
  }, [layout.landscape, layout.controls, camera.status])
  // "More" (voice guide, an example, the phone's own camera): focus goes
  // in, and back to the button.
  const moreButton = useRef<HTMLButtonElement>(null)
  const shutterRef = useRef<HTMLButtonElement>(null)
  const moreWasOpen = useRef(false)
  useEffect(() => {
    if (!moreOpen && moreWasOpen.current && !showExample) moreButton.current?.focus({ preventScroll: true })
    moreWasOpen.current = moreOpen
  }, [moreOpen, showExample])
  // The example (opened from More) closes: back to More.
  const exampleWasOpen = useRef(false)
  useEffect(() => {
    if (!showExample && exampleWasOpen.current) moreButton.current?.focus({ preventScroll: true })
    exampleWasOpen.current = showExample
  }, [showExample])
  // "Turn your phone sideways": put away for this camera visit when asked.
  const [keepUpright, setKeepUpright] = useState(false)
  // "Where to stand" (the view drawing opens it): focus goes in, and back.
  const [standOpen, setStandOpen] = useState(false)
  const standDone = useRef<HTMLButtonElement>(null)
  const pictogram = useRef<HTMLButtonElement>(null)
  const standWasOpen = useRef(false)
  useEffect(() => {
    if (standOpen) standDone.current?.focus({ preventScroll: true })
    else if (standWasOpen.current) pictogram.current?.focus({ preventScroll: true })
    standWasOpen.current = standOpen
  }, [standOpen])

  // The wide lens (0.5×), where the phone has one: theirs to switch on for
  // the whole-car photos; close-ups and the VIN go back to the usual lens
  // (sharper up close), and the next whole-car photo gets their choice back.
  const wantWide = useRef(false)
  const { setLens } = camera
  const shotKind = shot?.kind
  useEffect(() => {
    if (shotKind) setLens(shotKind === "wide" && wantWide.current ? "wide" : "normal")
  }, [shotKind, setLens])

  const chooseLens = (lens: Lens) => {
    wantWide.current = lens === "wide"
    setLens(lens)
  }

  const snapRef = useRef<(source: "auto" | "manual") => void>(() => {})
  const live = useLiveCoach({
    video: camera.videoRef.current,
    running,
    shot: shot ?? plan[0],
    attempt,
    firstShot,
    vision,
    screen: size,
    insets,
    torch: camera.torch.supported,
    room: {
      // (The button is on screen whenever a whole-car photo is.)
      widen: camera.wideLabel && camera.lens === "normal" ? camera.wideLabel : null,
      canTurn: !layout.landscape && !keepUpright,
    },
    onAutoSnap: () => snapRef.current("auto"),
  })

  // The intro: at the start of a whole-car photo, while nothing is framed
  // yet (vision that arrives mid-framing waits for the next photo).
  const lockPhase = live.lockPhase
  useEffect(() => {
    if (introSeen || introFor === shotId || walkCard || taking || strip) return
    if (!vision || shotKind !== "wide" || camera.status !== "live") return
    if (lockPhase === null || lockPhase === "searching") setIntroFor(shotId ?? null)
  }, [introSeen, introFor, shotId, shotKind, walkCard, taking, strip, vision, camera.status, lockPhase])
  // It goes: the shutter takes the focus.
  const introWasUp = useRef(false)
  useEffect(() => {
    if (!introUp && introWasUp.current) shutterRef.current?.focus({ preventScroll: true })
    introWasUp.current = introUp
  }, [introUp])

  // How this photo was taken, for tuning the camera from real use: how long
  // the camera was up for it before the shutter (cards and sheets over it
  // don't count), and how fast the models ran.
  const upMs = useRef(0)
  const upSince = useRef<number | null>(null)
  const runningNow = useRef(running)
  useLayoutEffect(() => {
    runningNow.current = running
  })
  useEffect(() => {
    upMs.current = 0
    upSince.current = runningNow.current ? performance.now() : null
  }, [shotId, attempt])
  useEffect(() => {
    if (running) upSince.current ??= performance.now()
    else if (upSince.current !== null) {
      upMs.current += performance.now() - upSince.current
      upSince.current = null
    }
  }, [running])
  const howTaken = useRef<() => Omit<IntakeShotCapture, "photo"> | null>(() => null)
  useLayoutEffect(() => {
    howTaken.current = () =>
      shot?.kind === "sticker"
        ? null
        : {
            lockMs:
              upSince.current === null && upMs.current === 0
                ? null
                : Math.round(upMs.current + (upSince.current === null ? 0 : performance.now() - upSince.current)),
            vision: vision?.mode ?? null,
            detectMs: live.debug.inferenceMs,
            segmentMs: live.debug.segmentMs,
            viewMs: live.debug.viewMs,
            orientation: layout.landscape ? "sideways" : "upright",
            lens: camera.lens,
          }
  })

  // It's been dark this shot: the light button shows (and stays).
  const dark = live.debug.luma > 0 && live.debug.luma < QUALITY.DARK_LUMA * 1.4
  useEffect(() => {
    if (dark) setTorchShown(true)
  }, [dark])

  // VIN step: look for the barcode a couple of times a second while nothing
  // else is on screen. A VIN is only taken when two reads in a row agree
  // (a smudged bar can still pass the check digit), and never one they've
  // already said "Not quite" to.
  useEffect(() => {
    if (!vinStepOn || camera.status !== "live" || walkCard || vinStep.busy || moreOpen) return
    let stop = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let last: string | null = null
    const tick = async () => {
      const video = camera.videoRef.current
      const reader = await loadVinReader()
      if (stop) return
      const vinFound = reader && video ? await reader.read(video) : null
      if (stop) return
      const confirmed = !!vinFound && vinFound === last
      if (confirmed && vinStep.found(vinFound, "scan")) return
      last = vinFound
      // Something new read: look again right away to confirm it. (One they
      // turned down: keep looking at the usual pace.)
      timer = setTimeout(() => void tick(), vinFound && !confirmed ? 120 : 400)
    }
    void tick()
    return () => {
      stop = true
      if (timer) clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vinStepOn, camera.status, walkCard, vinStep.busy, moreOpen])

  // The walk, from the phone's motion sensors: the direction at each
  // whole-car photo (this camera session), so the next one knows how far the
  // person turned, and the live dot on the little map knows where they are.
  const yaw = useCameraYaw(camera.status === "live")
  const wideYaws = useRef<Array<{ corner: Corner; yaw: number }>>([])
  const turnFor = (corner: Corner): TurnJudgement | null => {
    const now = yaw.read()
    const last = [...wideYaws.current].reverse().find((w) => w.corner !== corner)
    if (now === null || !last) return null
    return judgeTurn(last.corner, corner, angleDiff(now, last.yaw))
  }
  const rememberYaw = (corner: Corner, at: number | null) => {
    if (at === null) return
    wideYaws.current = [...wideYaws.current.filter((w) => w.corner !== corner), { corner, yaw: at }]
  }
  // The sensors restarted (screen locked, app switched): their zero moved,
  // so earlier readings no longer compare with new ones.
  useEffect(() => {
    wideYaws.current = []
  }, [yaw.epoch])
  const first = wideYaws.current[0] ?? null
  const here =
    shot?.kind === "wide" && first && yaw.live !== null ? bearingNow(first.corner, first.yaw, yaw.live) : null

  // While a photo's result is up, the result does the talking. On the VIN
  // step: where to point, unless the light (dark, a reflection) is the problem.
  const coachLine =
    camera.status !== "live" || strip
      ? ""
      : exposing
        ? COACH_LINE.holdStill
        : vinStepOn
        ? vinStep.state.kind === "idle"
          ? (vinStep.state.note ?? (live.coach.tone === "warn" ? live.coach.line : VIN_COPY.scanning))
          : ""
        : live.coach.line

  // The band is saying how to make room (the wide lens, or turning
  // sideways): the sideways pill steps aside (one suggestion at a time).
  const roomHint =
    coachLine === COACH_LINE.turnSideways || (!!camera.wideLabel && coachLine === wideLensLine(camera.wideLabel))

  const intro = shot ? `${walkLine(shot)}. ${shotInstruction(shot)}` : ""
  const { announce } = useVoiceCoach({
    enabled: voice && camera.status === "live",
    shotKey: `${shot?.id ?? ""}:${attempt}`,
    intro,
    line: running ? coachLine : "",
  })
  const [canSpeak] = useState(speechAvailable)
  const toggleVoice = () => {
    const next = !voice
    onVoiceChange(next)
    // Spoken inside the tap: that's what lets iOS talk at all.
    if (next) announce(`Voice guide on. ${intro}`)
    else if (canSpeak) window.speechSynthesis.cancel()
  }

  // A light buzz when the brackets lock (Android; iOS has no web vibration).
  const prevPhase = useRef(live.lockPhase)
  useEffect(() => {
    if (live.lockPhase === "locked" && prevPhase.current !== "locked") {
      try {
        navigator.vibrate?.(12)
      } catch {
        // ignore
      }
    }
    prevPhase.current = live.lockPhase
  }, [live.lockPhase])

  const snap = useCallback(
    async (kind: "auto" | "manual") => {
      if (!shot || busy.current || strip || camera.status !== "live" || camera.switching) return
      busy.current = true
      setTaking(true)
      try {
        // Read where the phone points (and how it got here) at the shutter,
        // not after the save.
        const turn = shot.kind === "wide" && shot.corner ? turnFor(shot.corner) : null
        const yawNow = yaw.read()
        const how = howTaken.current()
        setError(null)
        setExposing(true)
        const photo = await camera.capture().finally(() => setExposing(false))
        // Skipped / went back while it was being taken: not for this step.
        if (shotIdRef.current !== shot.id) return
        if (!photo) {
          // The camera gave nothing (a phone short on memory): say so and
          // start the step over, so the auto-photo can fire again.
          setError(C.unreadable)
          setAttempt((a) => a + 1)
          return
        }
        // The shutter the person sees and feels goes when the photo is
        // really taken (a still can take a moment): moving at the flash
        // can't spoil it.
        if (!reduce) setFlash(true)
        try {
          navigator.vibrate?.(18)
        } catch {
          // ignore
        }
        const prepared = await preparePhoto(
          photo.blob,
          `${shot.id.replace(":", "-")}-${Date.now()}`,
          photo.via === "still" ? { shape: photo.frame } : {},
        )
        // Skipped / went back while it was saving: this photo isn't for the
        // step on screen any more. Drop it.
        if (shotIdRef.current !== shot.id) {
          setFlash(false)
          return
        }
        if (!prepared.ok) {
          setFlash(false)
          setError(C.unreadable)
          setAttempt((a) => a + 1)
          return
        }
        if (shot.kind === "wide" && shot.corner) rememberYaw(shot.corner, yawNow)
        uploads.submit(shot.id, prepared.photo.file, {
          source: kind,
          planned: shot,
          turn,
          capture: how && { ...how, photo: photo.via },
        })
        setHeld(false)
        // The VIN step answers with its own card (reading → "Is it a …?").
        if (shot.kind === "sticker") vinStep.photo(prepared.photo.file)
        else setStrip({ key: shot.id, metrics: prepared.photo.metrics })
        const tray = trayRef.current?.getBoundingClientRect()
        if (tray && !reduce) {
          setFlying((prev) => {
            if (prev) URL.revokeObjectURL(prev.url)
            return { url: URL.createObjectURL(prepared.photo.file), to: tray }
          })
        }
      } finally {
        busy.current = false
        setTaking(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shot, strip, camera, uploads, reduce, vinStep.photo],
  )
  useLayoutEffect(() => {
    snapRef.current = (kind) => void snap(kind)
  })

  // A photo still flying to the tray when the camera closes.
  const flyingRef = useRef(flying)
  useLayoutEffect(() => {
    flyingRef.current = flying
  })
  useEffect(
    () => () => {
      if (flyingRef.current) URL.revokeObjectURL(flyingRef.current.url)
    },
    [],
  )

  useEffect(() => {
    if (!flash) return
    const t = setTimeout(() => setFlash(false), 90)
    return () => clearTimeout(t)
  }, [flash])

  // The strip's own photo (the strip always belongs to the shot that took it).
  const record = strip ? capture.shots[strip.key as ShotId] : undefined
  const result: ResultState | null = strip ? resultFor(record, localIssue(strip.metrics, shot)) : null

  // A clean photo moves on by itself unless the person touched the strip.
  const nextRef = useRef(onNext)
  useLayoutEffect(() => {
    nextRef.current = onNext
  })
  useEffect(() => {
    if (!result || held || moreOpen) return
    if (result.kind !== "good" && result.kind !== "queued") return
    const t = setTimeout(() => nextRef.current(), AUTO_NEXT_MS)
    return () => clearTimeout(t)
  }, [result?.kind, held, moreOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const retake = () => {
    setStrip(null)
    setHeld(false)
    setAttempt((a) => a + 1)
  }

  // Close with Escape on a keyboard (with the VIN card up, Escape closes just
  // the card); system Back is handled by the flow frame.
  const escapeRef = useRef(onClose)
  useLayoutEffect(() => {
    escapeRef.current = introUp
      ? onIntroSeen
      : showExample
      ? () => setShowExample(false)
      : moreOpen
        ? () => setMoreOpen(false)
        : standOpen
        ? () => setStandOpen(false)
        : vinCardUp
          ? vinStep.cancel
          : onClose
  })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") escapeRef.current()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  const taken = useMemo(
    () => queue.map((id) => capture.shots[id]).filter((r): r is NonNullable<typeof r> => !!r),
    [queue, capture.shots],
  )

  if (!shot) return null
  const example = exampleFor(shot)
  const guide = guideBox(shot.kind, size, insets)
  // Whole car (smart camera): the brackets mark the target, and hug their
  // car once it fits. Closer: they follow the car. Otherwise a fixed guide.
  const locked = live.lockPhase === "locked" || live.lockPhase === "snapped"
  const fits = !!live.fit?.pass && !!live.car.box
  const tracking =
    shot.kind === "wide" ? fits : !!live.box && shot.kind === "closer" && !!vision
  const tone: BracketTone = locked ? "locked" : tracking ? "tracking" : "guide"
  const bracketBox =
    shot.kind === "wide" && live.target
      ? fits && live.car.box
        ? live.car.box
        : live.target.box
      : tracking && live.box
        ? live.box
        : guide
  const progressValue = taken.length / Math.max(1, queue.length)
  const problem = camera.status === "denied" || camera.status === "unavailable"
  // Cards over the picture (the VIN, the photo just taken): along the bottom
  // upright; between the instruction column and the rail sideways.
  const panelArea = layout.landscape
    ? {
        left: `max(${insets.left + 8}px, env(safe-area-inset-left))`,
        right: `max(${insets.right + 8}px, env(safe-area-inset-right))`,
        bottom: "max(env(safe-area-inset-bottom), 12px)",
      }
    : { left: 12, right: 12, bottom: "max(env(safe-area-inset-bottom), 12px)" }

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label="Camera"
      className="fixed inset-0 z-50 overflow-hidden bg-black text-white"
      data-camera-status={camera.status}
      data-camera-mode={vision ? "smart" : "guided"}
      data-vision={vision?.mode}
    >
      <video
        ref={camera.videoRef}
        autoPlay
        muted
        playsInline
        className="absolute inset-0 size-full object-cover"
        aria-hidden
      />
      {/* Switching lens: the last picture, softened, until the new one plays. */}
      {camera.poster && (
        <img src={camera.poster} alt="" aria-hidden className="absolute inset-0 size-full scale-110 object-cover blur-xl" />
      )}

      {camera.status === "live" && size.w > 0 && (
        <>
          {shot.kind === "wide" && running && live.car.outline && (
            <CarHighlight points={live.car.outline} width={size.w} height={size.h} tone={tone} />
          )}
          <ViewfinderBrackets box={bracketBox} width={size.w} height={size.h} tone={tone} />
        </>
      )}

      {(() => {
        const side = layout.landscape ? layout.controls : "bottom"
        // The light: only where the phone has one, and it's dark (or it's on).
        const torch = camera.torch.supported && (camera.torch.on || torchShown) && (
          <button
            type="button"
            onClick={() => void camera.setTorch(!camera.torch.on)}
            aria-pressed={camera.torch.on}
            aria-label={camera.torch.on ? "Turn the light off" : "Turn the light on"}
            className={cx(
              "grid size-11 shrink-0 place-items-center rounded-full backdrop-blur-md focus-visible:outline-2 focus-visible:outline-white",
              camera.torch.on ? "bg-white text-ink" : "bg-black/40",
            )}
          >
            {camera.torch.on ? <Zap className="size-5" aria-hidden /> : <ZapOff className="size-5" aria-hidden />}
          </button>
        )
        const topRow = (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close the camera"
              className="grid size-11 shrink-0 place-items-center rounded-full bg-black/40 backdrop-blur-md focus-visible:outline-2 focus-visible:outline-white"
            >
              <XClose className="size-6" aria-hidden />
            </button>
            <div
              className="h-1 flex-1 overflow-hidden rounded-full bg-white/25"
              role="progressbar"
              aria-label="Photos taken"
              aria-valuemin={0}
              aria-valuemax={queue.length}
              aria-valuenow={taken.length}
              aria-valuetext={`${taken.length} of ${queue.length} photos`}
            >
              <motion.div
                className="h-full origin-left rounded-full bg-white"
                initial={false}
                animate={{ scaleX: progressValue }}
                transition={{ type: "spring", stiffness: 260, damping: 32 }}
              />
            </div>
            {torch || <span className="size-11 shrink-0" aria-hidden />}
          </div>
        )
        // The instruction on a dark band: readable over any scene.
        const band = (
          <div className="rounded-2xl bg-black/55 px-4 py-3 backdrop-blur-md">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h2
                  className={cx(
                    "font-semibold leading-tight tracking-tight text-white",
                    layout.landscape ? "text-[18px]" : "text-[22px]",
                  )}
                >
                  {shotTitle(shot)}
                </h2>
                <p
                  data-testid="coach-line"
                  data-tone={live.coach.tone}
                  className={cx(
                    "mt-1 font-medium leading-snug text-white",
                    layout.landscape ? "min-h-[41px] text-[15px]" : "min-h-[47px] text-[17px]",
                  )}
                >
                  {coachLine && (
                    <span
                      aria-hidden
                      className={cx(
                        "mb-0.5 mr-2 inline-block size-2.5 rounded-full align-middle",
                        live.coach.tone === "good" ? "bg-signal-on-dark" : live.coach.tone === "warn" ? "bg-amber-400" : "bg-white/70",
                      )}
                    />
                  )}
                  {coachLine}
                </p>
              </div>
              {shot.kind === "wide" && shot.corner && (
                <ViewPictogram
                  ref={pictogram}
                  corner={shot.corner}
                  body={bodyType}
                  open={standOpen}
                  onToggle={() => setStandOpen((o) => !o)}
                />
              )}
            </div>
          </div>
        )
        const checks = camera.status === "live" && !strip && <Checks checks={live.checks} compact={layout.landscape} />
        // The wide lens (0.5×) where the phone has one, like the phone's own
        // camera: on every whole-car photo (for want of room to back up).
        const lensSwitch = camera.wideLabel && shot.kind === "wide" && camera.status === "live" && !strip && (
          <LensSwitch lens={camera.lens} wideLabel={camera.wideLabel} disabled={camera.switching || taking} onChange={chooseLens} />
        )
        const errorLine = error && (
          <p role="alert" className="mb-3 text-center text-[15px] font-medium text-amber-300">
            {error}
          </p>
        )
        const tray = (
          <button
            ref={trayRef}
            type="button"
            onClick={onClose}
            aria-label={`${C.review} (${taken.length})`}
            className="relative size-14 shrink-0 rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {taken.slice(-3).map((r, i, arr) => (
              <img
                key={r.key}
                src={r.previewUrl}
                alt=""
                className="absolute inset-0 size-14 rounded-xl object-cover ring-2 ring-white/90"
                style={{ transform: `rotate(${(i - (arr.length - 1)) * 6}deg)` }}
              />
            ))}
            {taken.length === 0 && <span className="absolute inset-0 rounded-xl border-2 border-dashed border-white/35" />}
            {taken.length > 0 && (
              <span className="absolute -right-1.5 -top-1.5 grid size-6 place-items-center rounded-full bg-white text-[13px] font-bold text-ink">
                {taken.length}
              </span>
            )}
          </button>
        )
        const shutter = (
          <Shutter
            ref={shutterRef}
            ready={live.coach.ready}
            progress={live.progress}
            taking={taking}
            disabled={camera.status !== "live" || camera.switching || !!strip || walkCard || (vinStepOn && vinStep.busy)}
            onPress={() => void snap("manual")}
          />
        )
        const more = (
          <button
            ref={moreButton}
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-label="More"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            className="grid size-14 shrink-0 place-items-center rounded-full bg-black/40 backdrop-blur-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <DotsHorizontal className="size-6" aria-hidden />
          </button>
        )
        const link = "h-11 rounded-xl px-2.5 text-[14px] font-semibold text-white/85 hover:text-white focus-visible:outline-2 focus-visible:outline-white"
        const links = (
          <div ref={linksRef} className={cx("flex flex-wrap items-center justify-center", vinCardUp && "invisible")}>
            {vinStepOn ? (
              <>
                <button type="button" onClick={vinStep.type} className={link}>
                  {VIN_COPY.type}
                </button>
                <span className="text-white/40" aria-hidden>
                  ·
                </span>
                <button type="button" onClick={() => onSkip(shot.id)} className={link}>
                  {VIN_COPY.skip}
                </button>
              </>
            ) : (
              <button type="button" onClick={() => onSkip(shot.id)} className={link}>
                {C.skip}
              </button>
            )}
          </div>
        )
        if (side === "bottom") {
          return (
            <>
              <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-black/70 via-black/35 to-transparent" />
              <div ref={topRef} className="absolute inset-x-0 top-0 px-4 pt-[max(env(safe-area-inset-top),12px)]">
                {topRow}
                <div className="mt-3">{band}</div>
              </div>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black/75 via-black/40 to-transparent" />
              {/* Hidden behind a camera problem, so only the problem's own ways out are reachable. */}
              <div
                ref={bottomRef}
                className="absolute inset-x-0 bottom-0 px-5 pb-[max(env(safe-area-inset-bottom),14px)]"
                hidden={problem}
              >
                {errorLine}
                {lensSwitch && <div className="mb-2.5 flex justify-center">{lensSwitch}</div>}
                {checks}
                <div className="grid grid-cols-[1fr_auto_1fr] items-center">
                  {tray}
                  {shutter}
                  <div className="justify-self-end">{more}</div>
                </div>
                <div className="mt-2">{links}</div>
              </div>
            </>
          )
        }
        // Sideways: the instruction column, and the shutter rail on the side
        // the phone's bottom is.
        const railOnRight = side === "right"
        return (
          <>
            {/* The column is always on the left of the picture (after the
                rail when the rail is on the left): it gets the shade. */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-[50%] bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
            <div
              ref={topRef}
              className={cx(
                "absolute inset-y-0 flex min-h-0 w-[min(280px,33%)] flex-col justify-between gap-2 overflow-y-auto pb-[max(env(safe-area-inset-bottom),10px)] pt-[max(env(safe-area-inset-top),10px)]",
                railOnRight
                  ? "left-0 pl-[max(env(safe-area-inset-left),12px)] pr-2"
                  : "left-[calc(92px+max(env(safe-area-inset-left),0px))] pl-2 pr-2",
              )}
            >
              <div>
                {topRow}
                <div className="mt-2">{band}</div>
              </div>
              <div hidden={problem}>
                {errorLine}
                {checks}
                {links}
              </div>
            </div>
            <div
              ref={bottomRef}
              hidden={problem}
              className={cx(
                "absolute inset-y-0 flex flex-col items-center justify-center bg-black/35 backdrop-blur-sm",
                lensSwitch ? "gap-5" : "gap-7",
                railOnRight
                  ? "right-0 w-[calc(92px+max(env(safe-area-inset-right),0px))] pr-[max(env(safe-area-inset-right),0px)]"
                  : "left-0 w-[calc(92px+max(env(safe-area-inset-left),0px))] pl-[max(env(safe-area-inset-left),0px)]",
              )}
            >
              {lensSwitch}
              {more}
              {shutter}
              {tray}
            </div>
          </>
        )
      })()}

      {/* Sideways for the whole car (asked, not forced); upright again for a
          close-up or the VIN. A pill over the top of the picture: the target
          doesn't move. */}
      {camera.status === "live" && !walkCard && !strip && !standOpen && !moreOpen && !vinCardUp && !introUp && (
        (shot.kind === "wide" && !layout.landscape && !keepUpright && !roomHint) ||
        (layout.landscape && (shot.kind === "closeup" || shot.kind === "sticker"))
      ) && (
        <div
          className="absolute z-20 flex justify-center"
          style={
            layout.landscape
              ? { top: 12, left: insets.left, right: insets.right }
              : { top: insets.top + 10, left: 12, right: 12 }
          }
        >
          {layout.landscape ? (
            <RotatePrompt mode="upright" />
          ) : (
            <RotatePrompt
              mode="sideways"
              onKeep={() => {
                setKeepUpright(true)
                // The × goes with the pill: the shutter takes the focus.
                requestAnimationFrame(() => shutterRef.current?.focus({ preventScroll: true }))
              }}
            />
          )}
        </div>
      )}

      {/* Where to stand, and (motion sensors) where you are now. Floats over
          the picture, so the target doesn't move. */}
      {standOpen && shot.kind === "wide" && (
        <div
          className="absolute z-20"
          style={
            layout.landscape
              ? {
                  top: "max(env(safe-area-inset-top), 12px)",
                  left: `max(${insets.left + 8}px, env(safe-area-inset-left))`,
                  right: `max(${insets.right + 8}px, env(safe-area-inset-right))`,
                }
              : { top: insets.top + 8, left: 16, right: 16 }
          }
        >
          <div
            id="stand-sheet"
            className="flex items-center gap-4 rounded-2xl bg-black/80 p-3 backdrop-blur-md"
            data-testid="stand-sheet"
          >
            <div className="w-24 shrink-0">
              <StandMap
                shot={shot}
                plan={plan}
                damage={damage}
                tone="dark"
                here={here}
                label={here === null ? "Where to stand" : "Where to stand, and where you are"}
              />
            </div>
            <p className="flex-1 text-[15px] leading-snug text-white/85">{walkLine(shot)}</p>
            <button
              type="button"
              ref={standDone}
              onClick={() => setStandOpen(false)}
              className="h-11 shrink-0 rounded-xl bg-white/15 px-4 text-[15px] font-semibold focus-visible:outline-2 focus-visible:outline-white"
            >
              Done
            </button>
          </div>
        </div>
      )}
      <p className="sr-only" aria-live="polite">
        {shotAnnouncement(shot, index, queue.length)}
      </p>
      <LiveAnnouncer line={vinCardUp ? vinAnnouncement(vinStep.state) : coachLine} />

      {camera.status === "starting" && (
        <div className="absolute inset-0 z-20 grid place-items-center">
          <span className="flex items-center gap-3 text-[16px] font-medium text-white/80">
            <span className="size-5 animate-spin rounded-full border-2 border-white/25 border-t-white" aria-hidden />
            Opening the camera…
          </span>
        </div>
      )}

      {(camera.status === "denied" || camera.status === "unavailable" || camera.status === "interrupted") && (
        <CameraProblem status={camera.status} onRetry={camera.restart} onUseNative={onUseNative} />
      )}

      <AnimatePresence>
        {walkCard && camera.status !== "denied" && camera.status !== "unavailable" && (
          <WalkCard
            key={shot.id}
            shot={shot}
            plan={plan}
            damage={damage}
            from={prevShot ? standFor(prevShot, plan) : null}
            onDone={() => setWalkCard(false)}
          />
        )}
        {introUp && shot.corner && <AutoPhotoIntro key="intro" corner={shot.corner} body={bodyType} onDone={onIntroSeen} />}
      </AnimatePresence>

      {/* Shutter flash + the photo flying into the tray. */}
      <AnimatePresence>
        {flash && (
          <motion.div
            key="flash"
            className="pointer-events-none absolute inset-0 z-40 bg-white"
            initial={{ opacity: 0.9 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16 }}
          />
        )}
      </AnimatePresence>
      {flying && size.w > 0 && (
        <motion.img
          src={flying.url}
          alt=""
          className="pointer-events-none absolute left-0 top-0 z-40 rounded-xl object-cover"
          style={{ width: size.w, height: size.h, transformOrigin: "0 0" }}
          initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
          animate={{ x: flying.to.left, y: flying.to.top, scale: flying.to.width / size.w, opacity: 0.9 }}
          transition={{ type: "spring", stiffness: 260, damping: 28 }}
          onAnimationComplete={() => {
            URL.revokeObjectURL(flying.url)
            setFlying(null)
          }}
        />
      )}

      {vinCardUp && (
        <div className="absolute z-30" style={panelArea}>
          <VinCard step={vinStep} tone="dark" />
        </div>
      )}

      {moreOpen && (
        <MoreSheet
          side={layout.landscape ? layout.controls : "bottom"}
          onClose={() => setMoreOpen(false)}
          items={[
            ...(canSpeak
              ? [{ key: "voice", label: "Voice guide", checked: voice, onSelect: toggleVoice } satisfies MoreItem]
              : []),
            ...(example
              ? [
                  {
                    key: "example",
                    label: "See an example",
                    onSelect: () => {
                      setMoreOpen(false)
                      setShowExample(true)
                    },
                  } satisfies MoreItem,
                ]
              : []),
            { key: "native", label: "Use my phone's camera", onSelect: onUseNative },
          ]}
        />
      )}

      {/* After the snap: the photo, what we think of it, and Retake / Next. */}
      <AnimatePresence>
        {strip && result && (
          <motion.div
            key="strip"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            onPointerDown={() => setHeld(true)}
            style={panelArea}
            className="absolute z-30 rounded-3xl bg-zinc-950/92 p-4 shadow-2xl ring-1 ring-white/10 backdrop-blur-xl"
            data-testid="review-strip"
            data-result={result.kind}
          >
            <div className="flex gap-3">
              {record?.previewUrl && (
                <img src={record.previewUrl} alt={`Your photo: ${shotTitle(shot)}`} className="h-20 w-16 shrink-0 rounded-xl object-cover" />
              )}
              <div className="min-w-0 flex-1" aria-live="polite">
                <StripLine result={result} shot={shot} description={record?.description ?? null} />
              </div>
            </div>
            <StripActions
              result={result}
              onRetake={retake}
              onKeep={() => {
                onAccept(shot.id)
                onNext()
              }}
              onRetry={() => uploads.retry(shot.id)}
              onNext={onNext}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showExample && example && (
          <motion.button
            type="button"
            key="example"
            onClick={() => setShowExample(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16 }}
            className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/90 px-6"
            aria-label="Close the example"
          >
            <img src={example.src} alt={example.alt} className="max-h-[70svh] w-auto rounded-2xl object-contain" />
            <p className="mt-4 text-[17px] font-semibold">{shotTitle(shot)}</p>
            <p className="mt-1 text-[14px] text-white/60">Tap anywhere to go back</p>
          </motion.button>
        )}
      </AnimatePresence>

      {recorderOn && WalkRecorderPanel && (
        <Suspense fallback={null}>
          <WalkRecorderPanel
            video={camera.videoRef.current}
            app={{
              shot: shot?.id ?? null,
              line: coachLine,
              lock: live.lockPhase,
              result: result?.kind ?? null,
              detectMs: live.debug.inferenceMs,
              segmentMs: live.debug.segmentMs,
            }}
          />
        </Suspense>
      )}

      {debug && (
        <div
          className="pointer-events-none absolute z-50 rounded-lg bg-black/70 p-2 font-mono text-[11px] leading-tight text-lime-300"
          style={layout.landscape ? { right: insets.right + 8, top: 8 } : { left: 12, bottom: 192 }}
          data-testid="camera-debug"
        >
          <div>
            mode {vision ? `smart (${vision.mode})` : "guided"} · {camera.status}
          </div>
          <div>luma {live.debug.luma} · sharp {live.debug.sharpness} · motion {live.debug.motion} · glare {live.debug.glare}</div>
          <div>infer {live.debug.inferenceMs ?? "-"}ms · score {live.debug.score?.toFixed(2) ?? "-"}</div>
          <div>lock {live.lockPhase ?? "-"} · ring {live.progress.toFixed(2)}</div>
          <div>
            view {live.view?.kind ?? "-"} {live.debug.viewMs ?? "-"}ms · seg {live.debug.segmentMs ?? "-"}ms · aspect {live.debug.aspect ?? "-"} · yaw {yaw.live?.toFixed(0) ?? "-"}
          </div>
          {live.box && (
            <div>
              box {live.box.x.toFixed(2)},{live.box.y.toFixed(2)} {live.box.w.toFixed(2)}×{live.box.h.toFixed(2)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Coaching lines for screen readers: only once a line has held for a second.
function LiveAnnouncer({ line }: { line: string }) {
  const [said, setSaid] = useState("")
  useEffect(() => {
    if (!line || line === said) return
    const t = setTimeout(() => setSaid(line), 1000)
    return () => clearTimeout(t)
  }, [line, said])
  return (
    <p className="sr-only" aria-live="polite">
      {said}
    </p>
  )
}

function Shutter({
  ref,
  ready,
  progress,
  taking,
  disabled,
  onPress,
}: {
  ref?: Ref<HTMLButtonElement>
  ready: boolean
  progress: number
  // While a photo is being taken the inner disc stays pressed in.
  taking: boolean
  disabled: boolean
  onPress: () => void
}) {
  const reduce = useReducedMotion()
  const R = 36
  const CIRC = 2 * Math.PI * R
  return (
    <motion.button
      ref={ref}
      type="button"
      onClick={onPress}
      disabled={disabled}
      whileTap={disabled ? undefined : { scale: 0.92 }}
      aria-label="Take the photo"
      aria-busy={taking || undefined}
      data-ready={ready}
      className="relative grid size-20 place-items-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white disabled:opacity-60"
    >
      <svg viewBox="0 0 80 80" className="absolute inset-0 size-20 -rotate-90" aria-hidden>
        <circle cx="40" cy="40" r={R} fill="none" strokeWidth="5" stroke={ready ? "var(--color-signal-on-dark)" : "rgba(255,255,255,0.9)"} />
        {progress > 0 && (
          <circle
            cx="40"
            cy="40"
            r={R}
            fill="none"
            strokeWidth="5"
            stroke="#ffffff"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - progress)}
            style={{ transition: "stroke-dashoffset 200ms linear" }}
          />
        )}
      </svg>
      <motion.span
        className="size-[60px] rounded-full bg-white"
        animate={taking ? { scale: reduce ? 1 : 0.84, opacity: 0.7 } : { scale: 1, opacity: 1 }}
        transition={{ duration: reduce ? 0 : 0.12, ease: [0.2, 0, 0, 1] }}
      />
    </motion.button>
  )
}

function StripLine({ result, shot, description }: { result: ResultState; shot: PlannedShot; description: string | null }) {
  switch (result.kind) {
    case "checking":
      return (
        <p className="flex items-center gap-2 text-[16px] font-medium text-white/85">
          <span className="size-4 animate-spin rounded-full border-2 border-white/25 border-t-white" aria-hidden />
          {C.checking}
        </p>
      )
    case "good":
      return result.phone ? (
        <p className="flex items-start gap-2 text-[16px] font-semibold leading-snug text-white" data-testid="phone-verdict">
          <Check className="mt-0.5 size-5 shrink-0 text-signal-on-dark" strokeWidth={3} aria-hidden />
          {phoneLine(shot, result.phone)}
        </p>
      ) : (
        <p className="text-[16px] leading-snug text-white/75">
          <span className="inline-flex items-center gap-1.5 font-semibold text-signal-on-dark">
            <Check className="size-4" strokeWidth={3} aria-hidden />
            {VERDICT_LINE.good}.
          </span>{" "}
          {description && <>We see: {description.replace(/\.$/, "").toLowerCase()}.</>}
        </p>
      )
    case "issue":
      return <p className="text-[16px] font-medium leading-snug text-amber-300">{VERDICT_LINE[result.verdict]}</p>
    case "wrong-side":
      return (
        <p className="text-[16px] font-medium leading-snug text-amber-300" data-testid="phone-verdict">
          {result.phone ? phoneLine(shot, result.phone) : wrongSideQuestion(shot)}
        </p>
      )
    case "queued":
      return <p className="text-[16px] font-medium leading-snug text-white/85">Saved. It will send when you're back online.</p>
    case "failed":
      return <p className="text-[16px] font-medium leading-snug text-amber-300">{C.failed}</p>
  }
}

function StripActions({
  result,
  onRetake,
  onKeep,
  onRetry,
  onNext,
}: {
  result: ResultState
  onRetake: () => void
  onKeep: () => void
  onRetry: () => void
  onNext: () => void
}) {
  const secondary =
    "flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl border border-white/25 text-[15px] font-semibold text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white"
  const primary =
    "flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-white text-[15px] font-semibold text-ink hover:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
  const retake = (
    <button type="button" onClick={onRetake} className={secondary}>
      <RefreshCw01 className="size-4" aria-hidden />
      {C.retake}
    </button>
  )
  let main: { label: string; onClick: () => void }
  // Something the phone is sure about: retake first; keeping it is the
  // quiet way out.
  if (result.kind === "issue" || (result.kind === "wrong-side" && result.phone)) {
    return (
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={onKeep} className={secondary}>
          {C.keep}
        </button>
        <button type="button" onClick={onRetake} className={primary}>
          <RefreshCw01 className="size-4" aria-hidden />
          {C.retake}
        </button>
      </div>
    )
  }
  if (result.kind === "failed") {
    return (
      <>
        <div className="mt-3 flex gap-2">
          {retake}
          <button type="button" onClick={onRetry} className={primary}>
            {C.retry}
          </button>
        </div>
        {/* Never stuck on a photo that won't send: fix it later from Review. */}
        <button
          type="button"
          onClick={onNext}
          className="mt-1 h-11 w-full rounded-xl text-[14px] font-semibold text-white/80 hover:text-white focus-visible:outline-2 focus-visible:outline-white"
        >
          {C.skipForNow}
        </button>
      </>
    )
  }
  if (result.kind === "wrong-side") main = { label: C.yesRight, onClick: onKeep }
  else main = { label: C.next, onClick: onNext }
  return (
    <div className="mt-3 flex gap-2">
      {retake}
      <button type="button" onClick={main.onClick} className={primary}>
        {main.label}
      </button>
    </div>
  )
}

// The three plain checks above the shutter. Each turns green as it passes.
// 0.5× / 1×: the lens in use is the white one. Each button's name starts
// with what it shows, so "Tap 0.5×" works by voice too.
function LensSwitch({
  lens,
  wideLabel,
  disabled,
  onChange,
}: {
  lens: Lens
  wideLabel: string
  disabled: boolean
  onChange: (lens: Lens) => void
}) {
  const options: Array<[Lens, string, string]> = [
    ["wide", wideLabel, "wide lens"],
    ["normal", "1×", "normal lens"],
  ]
  return (
    <div
      role="group"
      aria-label="Lens"
      data-testid="lens-switch"
      data-lens={lens}
      className="flex gap-1 rounded-full bg-black/50 p-1 backdrop-blur-md"
    >
      {options.map(([value, label, name]) => (
        <button
          key={value}
          type="button"
          aria-pressed={lens === value}
          disabled={disabled}
          onClick={() => onChange(value)}
          className={cx(
            // 36px to see, 44px to hit.
            "relative grid size-9 place-items-center rounded-full text-[13px] font-semibold tabular-nums transition-colors duration-150 before:absolute before:-inset-1 before:content-[''] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-60",
            lens === value ? "bg-white text-ink" : "text-white hover:bg-white/15",
          )}
        >
          {label}
          <span className="sr-only"> {name}</span>
        </button>
      ))}
    </div>
  )
}

function Checks({
  checks,
  compact = false,
}: {
  checks: { spot: boolean | null; sharp: boolean; light: boolean }
  // Sideways, in the instruction column: smaller, and they may wrap as pills.
  compact?: boolean
}) {
  const items: Array<[string, boolean]> = [
    ...(checks.spot === null ? [] : ([["Right spot", checks.spot]] as Array<[string, boolean]>)),
    ["Sharp", checks.sharp],
    ["Good light", checks.light],
  ]
  return (
    <ul
      className={cx("mb-3 flex gap-2", compact ? "flex-wrap justify-start gap-1.5" : "justify-center")}
      aria-label="Photo checks"
      data-testid="photo-checks"
    >
      {items.map(([label, ok]) => (
        <li
          key={label}
          data-ok={ok}
          className={cx(
            "flex items-center gap-1.5 whitespace-nowrap rounded-full font-semibold backdrop-blur-md transition-colors duration-150",
            compact ? "h-8 px-2.5 text-[13px]" : "h-9 px-3 text-[14px]",
            ok ? "bg-signal-on-dark/90 text-pine" : "bg-black/55 text-white",
          )}
        >
          {ok ? (
            <Check className="size-4" strokeWidth={3} aria-hidden />
          ) : (
            <span className="size-3.5 rounded-full border-2 border-white/60" aria-hidden />
          )}
          {label}
          <span className="sr-only">{ok ? ": yes" : ": not yet"}</span>
        </li>
      ))}
    </ul>
  )
}
